import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  LinearProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Container,
} from '@mui/material';
import {
  Add,
  Email,
  Phone,
  Person,
  Schedule,
  MoreVert,
  ArrowBack,
  Edit,
  Delete,
  NoteAlt,
  History,
} from '@mui/icons-material';
import { suppliersAPI } from '../../services/API';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';

// Styled components for better visual appeal
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: '0 8px 32px 0 rgba(0,0,0,0.15)',
  },
}));

const GradientAvatar = styled(Avatar)(({ theme, color }) => ({
  background: `linear-gradient(135deg, ${theme.palette[color].main}, ${theme.palette[color].dark})`,
  width: 56,
  height: 56,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  borderRadius: 12,
}));

const AddNoteButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '12px 24px',
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
  },
  transition: 'all 0.3s ease',
}));

export const InvSupplierCommunications = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCommunication, setSelectedCommunication] = useState(null);

  useEffect(() => {
    fetchCommunications();
  }, [id]);

  const fetchCommunications = async () => {
    try {
      const response = await suppliersAPI.getCommunications(id);
      setCommunications(response.data.data.communications);
    } catch (error) {
      enqueueSnackbar('Error fetching communications', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      await suppliersAPI.addCommunication(id, {
        type: 'note',
        content: newNote,
        date: new Date().toISOString(),
      });
      enqueueSnackbar('Note added successfully', { variant: 'success' });
      setNewNote('');
      fetchCommunications();
    } catch (error) {
      enqueueSnackbar('Error adding note', { variant: 'error' });
    }
  };

  const handleMenuOpen = (event, communication) => {
    setAnchorEl(event.currentTarget);
    setSelectedCommunication(communication);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCommunication(null);
  };

  const handleDelete = async () => {
    if (!selectedCommunication) return;

    try {
      await suppliersAPI.deleteCommunication(id, selectedCommunication._id);
      enqueueSnackbar('Communication deleted successfully', { variant: 'success' });
      handleMenuClose();
      fetchCommunications();
    } catch (error) {
      enqueueSnackbar('Error deleting communication', { variant: 'error' });
    }
  };

  const getCommunicationIcon = (type) => {
    switch (type) {
      case 'email':
        return <Email sx={{ fontSize: '1.5rem' }} />;
      case 'phone':
        return <Phone sx={{ fontSize: '1.5rem' }} />;
      case 'meeting':
        return <Person sx={{ fontSize: '1.5rem' }} />;
      case 'note':
        return <NoteAlt sx={{ fontSize: '1.5rem' }} />;
      default:
        return <Email sx={{ fontSize: '1.5rem' }} />;
    }
  };

  const getCommunicationColor = (type) => {
    switch (type) {
      case 'email':
        return 'primary';
      case 'phone':
        return 'success';
      case 'meeting':
        return 'warning';
      case 'note':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
      </Box>
    );
  }

  return (<Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back button and header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/hospital/hospital-inventory/suppliers')}
          variant="outlined"
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          Back to Suppliers
        </Button>
        <Box>
          <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Supplier Communications
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.2rem', mt: 1 }}>
            Manage and track all supplier interactions
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Add New Note */}
        <Grid item xs={12}>
          <StyledCard>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <NoteAlt sx={{ fontSize: '2rem', color: 'primary.main' }} />
                <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
                  Add New Note
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Type your communication notes here... ✍️"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      fontSize: '1.1rem',
                      '& textarea': {
                        fontSize: '1.1rem',
                        lineHeight: 1.6,
                      },
                    },
                  }}
                />
                <AddNoteButton
                  variant="contained"
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                  startIcon={<Add sx={{ fontSize: '1.5rem' }} />}
                >
                  Add Note
                </AddNoteButton>
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>

        {/* Communications List */}
        <Grid item xs={12}>
          <StyledCard>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <History sx={{ fontSize: '2rem', color: 'primary.main' }} />
                <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
                  Communication History
                </Typography>
                <StyledChip 
                  label={`${communications.length} items`} 
                  color="primary" 
                  variant="outlined" 
                />
              </Box>
              
              {communications.length === 0 ? (
                <Alert 
                  severity="info" 
                  sx={{ 
                    borderRadius: 3,
                    fontSize: '1.1rem',
                    py: 2,
                    '& .MuiAlert-message': {
                      fontSize: '1.1rem',
                    }
                  }}
                >
                  No communications recorded yet. Start by adding your first note above!
                </Alert>
              ) : (
                <Paper 
                  elevation={0} 
                  sx={{ 
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden'
                  }}
                >
                  <List sx={{ p: 0 }}>
                    {communications.map((communication, index) => (
                      <React.Fragment key={communication._id}>
                        <ListItem 
                          alignItems="flex-start" 
                          sx={{ 
                            px: 4, 
                            py: 3,
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            }
                          }}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              onClick={(e) => handleMenuOpen(e, communication)}
                              sx={{ 
                                borderRadius: 2,
                                '&:hover': {
                                  backgroundColor: 'action.active',
                                }
                              }}
                            >
                              <MoreVert sx={{ fontSize: '1.5rem' }} />
                            </IconButton>
                          }
                        >
                          <ListItemAvatar sx={{ minWidth: 72 }}>
                            <GradientAvatar color={getCommunicationColor(communication.type)}>
                              {getCommunicationIcon(communication.type)}
                            </GradientAvatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Typography 
                                  variant="h6" 
                                  fontWeight="bold" 
                                  sx={{ fontSize: '1.3rem' }}
                                >
                                  {communication.subject || 'Note'}
                                </Typography>
                                <StyledChip
                                  label={communication.type}
                                  size="medium"
                                  color={getCommunicationColor(communication.type)}
                                />
                                {communication.followUpRequired && (
                                  <StyledChip
                                    label="Follow Up Required"
                                    size="medium"
                                    color="warning"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography 
                                  variant="body1" 
                                  color="text.primary" 
                                  paragraph
                                  sx={{ 
                                    fontSize: '1.1rem',
                                    lineHeight: 1.7,
                                    mb: 2
                                  }}
                                >
                                  {communication.content}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Schedule fontSize="medium" color="action" />
                                    <Typography 
                                      variant="body1" 
                                      color="text.secondary"
                                      sx={{ fontSize: '1rem', fontWeight: 500 }}
                                    >
                                      {new Date(communication.date).toLocaleString()}
                                    </Typography>
                                  </Box>
                                  {communication.createdBy && (
                                    <Typography 
                                      variant="body1" 
                                      color="text.secondary"
                                      sx={{ fontSize: '1rem', fontWeight: 500 }}
                                    >
                                      By {communication.createdBy.name}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            }
                            sx={{
                              '& .MuiListItemText-primary': { mb: 1 },
                              '& .MuiListItemText-secondary': { width: '100%' }
                            }}
                          />
                        </ListItem>
                        {index < communications.length - 1 && (
                          <Divider 
                            variant="inset" 
                            component="li" 
                            sx={{ 
                              ml: 18,
                              mr: 4
                            }} 
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            mt: 1,
          }
        }}
      >
        <MenuItem 
          onClick={handleMenuClose}
          sx={{ 
            fontSize: '1.1rem',
            py: 2,
            px: 3
          }}
        >
          <Edit sx={{ mr: 2, fontSize: '1.3rem' }} />
          Edit Communication
        </MenuItem>
        <MenuItem 
          onClick={handleDelete}
          sx={{ 
            fontSize: '1.1rem',
            py: 2,
            px: 3,
            color: 'error.main'
          }}
        >
          <Delete sx={{ mr: 2, fontSize: '1.3rem' }} />
          Delete Communication
        </MenuItem>
      </Menu>
    </Container>
    </Box>
  );
};